import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Plus, Search, Edit2, Trash2, FileText, Award, X, ShieldCheck, Loader2, Mail, RefreshCw, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const STATUSES = ["Ongoing", "Cancelled", "Successfully Completed"];
const TYPES = ["Project Intern", "Research Intern", "Engineering Intern", "AI Intern", "Drone Intern", "Robotics Intern"];
const MODES = ["On-site", "Remote", "Hybrid"];

const EMPTY = {
  name: "", institute: "", internship_type: TYPES[0], role: "", mode: MODES[0],
  contact: "", email: "", start_date: "", end_date: "", issue_date: "", department: "", notes: "", status: STATUSES[0],
};

export default function OwnerInternDashboard({ refreshTick }) {
  const { refresh } = useAuth();
  const [interns, setInterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // intern obj or "new"
  const [showOwnerEmail, setShowOwnerEmail] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/interns");
      setInterns(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  const filtered = interns.filter((i) => {
    const s = (q || "").toLowerCase();
    return !s ||
      i.name?.toLowerCase().includes(s) ||
      i.credential_id?.toLowerCase().includes(s) ||
      i.role?.toLowerCase().includes(s) ||
      i.institute?.toLowerCase().includes(s);
  });

  const handleDelete = async (intern) => {
    if (!window.confirm(`Delete ${intern.name} (${intern.credential_id})?`)) return;
    try {
      await api.delete(`/interns/${intern.id}`);
      toast.success("Intern deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadPdf = (intern, kind) => {
    const url = `${API}/interns/${intern.id}/${kind}.pdf`;
    // Open in new tab; cookies / Bearer will be sent via the same origin. Fallback: use fetch with auth.
    fetch(url, { credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem('vj_token') || ''}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error("Failed to generate PDF");
        const blob = await r.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${kind === "offer-letter" ? "OfferLetter" : "Certificate"}-${intern.credential_id}.pdf`;
        document.body.appendChild(link); link.click(); link.remove();
      })
      .catch(() => toast.error("Could not download PDF"));
  };

  return (
    <section className="px-6 lg:px-12 py-12 bg-[#0B1120]" data-testid="owner-dashboard">
      <div className="max-w-[1400px]">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline text-slate-400 flex items-center gap-2">
              <ShieldCheck size={12} className="text-emerald-400"/> Owner Mode · Hidden from Public
            </div>
            <h2 className="font-display text-3xl font-extrabold text-white tracking-tight mt-2">Intern Management Console</h2>
            <p className="text-slate-400 text-sm mt-2 max-w-2xl">
              Create, edit, and manage all VAJRON intern credentials. Changes reflect instantly on the public verification system.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button className="vj-btn-ghost-dark" onClick={() => setShowChangePwd(true)} data-testid="owner-change-pwd-btn">
              <KeyRound size={14}/> Change Password
            </button>
            <button className="vj-btn-ghost-dark" onClick={() => setShowOwnerEmail(true)} data-testid="owner-change-email-btn">
              <Mail size={14}/> Owner Email
            </button>
            <button className="vj-btn-ghost-dark" onClick={load} data-testid="owner-refresh-btn">
              <RefreshCw size={14}/> Refresh
            </button>
            <button className="vj-btn-primary" onClick={() => setEditing("new")} data-testid="owner-create-intern-btn">
              <Plus size={14}/> Create Intern
            </button>
          </div>
        </div>

        <div className="vj-card-dark p-4 flex items-center gap-3 mb-5">
          <Search size={14} className="text-slate-500"/>
          <input
            data-testid="owner-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, credential, role, institute…"
            className="flex-1 bg-transparent border-0 outline-none text-sm text-slate-100 placeholder:text-slate-500"
          />
          <span className="vj-chip-dark">{filtered.length} interns</span>
        </div>

        <div className="vj-card-dark overflow-hidden">
          <div className="overflow-x-auto">
            <table className="vj-table" style={{ background: "#111827" }}>
              <thead>
                <tr style={{ background: "#0B1120" }}>
                  <th style={{ color: "#94A3B8", borderColor: "#1F2937", background: "#0B1120" }}>Credential</th>
                  <th style={{ color: "#94A3B8", borderColor: "#1F2937", background: "#0B1120" }}>Name</th>
                  <th style={{ color: "#94A3B8", borderColor: "#1F2937", background: "#0B1120" }}>Role</th>
                  <th style={{ color: "#94A3B8", borderColor: "#1F2937", background: "#0B1120" }}>Type · Mode</th>
                  <th style={{ color: "#94A3B8", borderColor: "#1F2937", background: "#0B1120" }}>Status</th>
                  <th style={{ color: "#94A3B8", borderColor: "#1F2937", background: "#0B1120" }} className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="text-center py-10" style={{color:"#94A3B8", borderColor:"#1F2937"}}>
                    <Loader2 className="inline animate-spin mr-2" size={14}/> Loading interns…
                  </td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10" style={{color:"#94A3B8", borderColor:"#1F2937"}}>No interns found.</td></tr>
                )}
                {!loading && filtered.map((i) => (
                  <tr key={i.id} data-testid={`intern-row-${i.credential_id}`}>
                    <td style={{borderColor:"#1F2937", color:"#fff"}}><span className="font-mono text-[12.5px] text-blue-200">{i.credential_id}</span></td>
                    <td style={{borderColor:"#1F2937", color:"#fff"}}>
                      <div className="text-white font-medium">{i.name}</div>
                      <div className="text-xs text-slate-400">{i.institute}</div>
                    </td>
                    <td style={{borderColor:"#1F2937", color:"#E5E7EB"}}>{i.role}<div className="text-xs text-slate-500">{i.department}</div></td>
                    <td style={{borderColor:"#1F2937", color:"#E5E7EB"}}>{i.internship_type} · {i.mode}</td>
                    <td style={{borderColor:"#1F2937"}}>
                      <StatusPill status={i.status}/>
                    </td>
                    <td style={{borderColor:"#1F2937"}}>
                      <div className="flex gap-1.5 justify-end">
                        <IconBtn onClick={() => downloadPdf(i, "offer-letter")} title="Offer Letter" testid={`offer-${i.credential_id}`}><FileText size={14}/></IconBtn>
                        <IconBtn onClick={() => downloadPdf(i, "certificate")} title="Certificate" testid={`certificate-${i.credential_id}`}><Award size={14}/></IconBtn>
                        <IconBtn onClick={() => setEditing(i)} title="Edit" testid={`edit-${i.credential_id}`}><Edit2 size={14}/></IconBtn>
                        <IconBtn onClick={() => handleDelete(i)} title="Delete" testid={`delete-${i.credential_id}`} danger><Trash2 size={14}/></IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editing && (
        <InternEditor
          initial={editing === "new" ? EMPTY : editing}
          isNew={editing === "new"}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {showOwnerEmail && (
        <OwnerEmailEditor onClose={() => setShowOwnerEmail(false)} onSaved={() => { setShowOwnerEmail(false); refresh(); }}/>
      )}

      {showChangePwd && <ChangePasswordModal onClose={() => setShowChangePwd(false)}/>}
    </section>
  );
}

function StatusPill({ status }) {
  const cls = status === "Successfully Completed" ? "vj-status-completed"
    : status === "Cancelled" ? "vj-status-cancelled" : "vj-status-ongoing";
  return <span className={`vj-status-pill ${cls}`}>{status}</span>;
}

function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={title}
      className={`h-8 w-8 inline-flex items-center justify-center border ${danger ? "border-red-900 text-red-300 hover:bg-red-950" : "border-slate-700 text-slate-200 hover:bg-slate-800"} transition-colors`}
    >
      {children}
    </button>
  );
}

function InternEditor({ initial, isNew, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const on = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (isNew) {
        await api.post("/interns", form);
        toast.success("Intern created");
      } else {
        await api.patch(`/interns/${initial.id}`, form);
        toast.success("Intern updated");
      }
      onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="vj-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="intern-editor-modal">
      <div className="vj-modal" style={{ maxWidth: 720 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-7 py-5 border-b border-slate-200">
          <div>
            <div className="overline">{isNew ? "Create Intern" : `Edit · ${initial.credential_id}`}</div>
            <h3 className="font-display text-xl font-bold mt-1">{isNew ? "New intern record" : "Update intern details"}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500" data-testid="editor-close-btn"><X size={18}/></button>
        </div>
        <form onSubmit={save} className="px-7 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Name" value={form.name} onChange={on("name")} required testid="ed-name"/>
          <F label="Institute" value={form.institute} onChange={on("institute")} required testid="ed-institute"/>
          <Sel label="Internship Type" value={form.internship_type} onChange={on("internship_type")} options={TYPES} testid="ed-type"/>
          <F label="Role" value={form.role} onChange={on("role")} required testid="ed-role"/>
          <Sel label="Mode" value={form.mode} onChange={on("mode")} options={MODES} testid="ed-mode"/>
          <F label="Department / Domain" value={form.department} onChange={on("department")} testid="ed-department"/>
          <F label="Contact Number" value={form.contact} onChange={on("contact")} testid="ed-contact"/>
          <F label="Email" type="email" value={form.email} onChange={on("email")} testid="ed-email"/>
          <F label="Start Date" type="date" value={form.start_date} onChange={on("start_date")} testid="ed-start"/>
          <F label="End Date" type="date" value={form.end_date} onChange={on("end_date")} testid="ed-end"/>
          <F label="Issue Date — Offer Letter & Certificate" type="date" value={form.issue_date} onChange={on("issue_date")} testid="ed-issue-date" className="md:col-span-2"/>
          <Sel label="Status" value={form.status} onChange={on("status")} options={STATUSES} testid="ed-status"/>
          <div className="md:col-span-2">
            <label className="overline">Notes</label>
            <textarea data-testid="ed-notes" value={form.notes} onChange={on("notes")} className="vj-input mt-1.5" rows={3}/>
          </div>
          {err && <div className="md:col-span-2 text-sm text-red-600">{err}</div>}
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" className="vj-btn-ghost" onClick={onClose} data-testid="editor-cancel-btn">Cancel</button>
            <button disabled={busy} className="vj-btn-primary" data-testid="editor-save-btn">
              {busy ? <Loader2 size={14} className="animate-spin"/> : null} {isNew ? "Create Intern" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function F({ label, testid, className = "", ...props }) {
  return (
    <div className={className}>
      <label className="overline">{label}</label>
      <input data-testid={testid} className="vj-input mt-1.5" {...props}/>
    </div>
  );
}
function Sel({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <select data-testid={testid} value={value} onChange={onChange} className="vj-input mt-1.5">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function OwnerEmailEditor({ onClose, onSaved }) {
  const [current, setCurrent] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/owner/current-email").then((r) => setCurrent(r.data.owner_email)).catch(()=>{});
  }, []);

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/owner/change-email", { new_email: newEmail });
      toast.success("Owner email updated. Sign in with the new email next time.");
      onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="vj-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="owner-email-modal">
      <div className="vj-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-7 py-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="overline">Owner Account</div>
            <h3 className="font-display text-xl font-bold mt-1">Change Owner Email</h3>
          </div>
          <button onClick={onClose} className="text-slate-500"><X size={18}/></button>
        </div>
        <form onSubmit={save} className="px-7 py-6 space-y-4">
          <div>
            <label className="overline">Current Owner Email</label>
            <div className="font-mono text-sm mt-1.5">{current || "—"}</div>
          </div>
          <div>
            <label className="overline">New Owner Email</label>
            <input data-testid="owner-new-email-input" type="email" required value={newEmail} onChange={(e)=>setNewEmail(e.target.value)} className="vj-input mt-1.5" placeholder="new.owner@example.com"/>
          </div>
          <p className="text-xs text-slate-500">The new owner must already have an account on VAJRON to access owner controls after logging in.</p>
          {err && <div className="text-sm text-red-600">{err}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" className="vj-btn-ghost" onClick={onClose}>Cancel</button>
            <button disabled={busy} className="vj-btn-primary" data-testid="owner-email-save-btn">{busy ? "Saving…" : "Update Owner"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [nw2, setNw2] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault(); setErr("");
    if (nw !== nw2) { setErr("New passwords do not match"); return; }
    if (nw.length < 6) { setErr("New password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated. Use the new password next time you log in.");
      onClose();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="vj-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="change-pwd-modal">
      <div className="vj-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-7 py-5 border-b border-slate-200 flex items-center justify-between">
          <div><div className="overline">Owner Account</div><h3 className="font-display text-xl font-bold mt-1">Change Password</h3></div>
          <button onClick={onClose} className="text-slate-500"><X size={18}/></button>
        </div>
        <form onSubmit={save} className="px-7 py-6 space-y-4">
          <div><label className="overline">Current Password</label><input data-testid="pwd-current" type="password" required value={cur} onChange={(e)=>setCur(e.target.value)} className="vj-input mt-1.5"/></div>
          <div><label className="overline">New Password</label><input data-testid="pwd-new" type="password" required minLength={6} value={nw} onChange={(e)=>setNw(e.target.value)} className="vj-input mt-1.5"/></div>
          <div><label className="overline">Confirm New Password</label><input data-testid="pwd-confirm" type="password" required value={nw2} onChange={(e)=>setNw2(e.target.value)} className="vj-input mt-1.5"/></div>
          {err && <div className="text-sm text-red-600">{err}</div>}
          <div className="flex justify-end gap-2"><button type="button" className="vj-btn-ghost" onClick={onClose}>Cancel</button><button disabled={busy} className="vj-btn-primary" data-testid="pwd-save">{busy ? "Saving…" : "Update Password"}</button></div>
        </form>
      </div>
    </div>
  );
}
